import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const roleColors = {
  superadmin: 'bg-purple-500',
  admin: 'bg-blue-500',
};
const statusColors = {
  active: 'bg-green-500',
  inactive: 'bg-gray-400',
};

export default function AdminsManagement() {
  const { token } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editAdmin, setEditAdmin] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: '', status: '', avatar: '' });
  const [deletingId, setDeletingId] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        const res = await fetch(`${baseUrl}/api/admin/admins`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch admins');
        const data = await res.json();
        // Map backend fields to frontend fields
        setAdmins(data.map(a => ({
          id: a.id || a._id,
          name: a.firstName && a.lastName ? `${a.firstName} ${a.lastName}` : a.name || '',
          email: a.email,
          role: a.role,
          status: a.status,
          avatar: a.avatarUrl || '',
        })));
      } catch (err) {
        setError(err.message || 'Error fetching admins');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchAdmins();
  }, [token]);

  const openEditModal = (admin) => {
    // Split name into firstName and lastName if possible
    let firstName = '', lastName = '';
    if (admin.name) {
      const parts = admin.name.split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    } else {
      firstName = admin.firstName || '';
      lastName = admin.lastName || '';
    }
    setForm({
      firstName,
      lastName,
      email: admin.email || '',
      phone: admin.phone || '',
      role: admin.role || '',
      status: admin.status || '',
      avatar: admin.avatar || admin.avatarUrl || '',
    });
    setEditAdmin(admin.id);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditAdmin(null);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };
  const handleSave = async () => {
    setUpdating(true);
    setError(null);
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${baseUrl}/api/admin/admins/${editAdmin}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          role: form.role,
          avatarUrl: form.avatar,
        }),
      });
      if (!res.ok) throw new Error('Failed to update admin');
      const updated = await res.json();
      setAdmins((prev) => prev.map((a) => (a.id === editAdmin ? {
        ...a,
        name: updated.firstName && updated.lastName ? `${updated.firstName} ${updated.lastName}` : updated.name || '',
        email: updated.email,
        phone: updated.phone,
        role: updated.role,
        status: updated.status,
        avatar: updated.avatarUrl || '',
      } : a)));
      closeModal();
    } catch (err) {
      setError(err.message || 'Error updating admin');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    setDeletingId(id);
    setError(null);
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${baseUrl}/api/admin/admins/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to delete admin');
      setAdmins((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err.message || 'Error deleting admin');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-2 sm:p-4 md:p-8 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Admin Management</h2>
      {loading ? (
        <div className="text-center py-8 text-lg">Loading admins...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">{error}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
          {admins.map((admin) => (
            <div key={admin.id} className="bg-white rounded-xl shadow-lg p-4 flex flex-col items-center">
              <img src={admin.avatar} alt={admin.name} className="w-16 h-16 rounded-full mb-2 border-2 border-gray-200 object-cover" />
              <div className="font-semibold text-lg text-center">{admin.name}</div>
              <div className="text-gray-500 text-sm mb-1 text-center">{admin.email}</div>
              <div className="flex gap-2 mb-2">
                <span className={`text-xs text-white px-2 py-1 rounded ${roleColors[admin.role]}`}>{admin.role}</span>
                <span className={`text-xs text-white px-2 py-1 rounded ${statusColors[admin.status]}`}>{admin.status}</span>
              </div>
              <button
                className="mt-2 px-4 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded shadow text-sm w-full"
                onClick={() => openEditModal(admin)}
              >
                Edit
              </button>
              <button
                className={`mt-2 px-4 py-1 bg-red-500 hover:bg-red-600 text-white rounded shadow text-sm w-full ${deletingId === admin.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                onClick={() => handleDelete(admin.id)}
                disabled={deletingId === admin.id}
              >
                {deletingId === admin.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-2 p-6 relative animate-fadeIn
            sm:mx-auto sm:w-96
            ">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl" onClick={closeModal}>&times;</button>
            <h3 className="text-xl font-bold mb-4">Edit Admin</h3>
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium">First Name
                <input name="firstName" value={form.firstName} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1" />
              </label>
              <label className="text-sm font-medium">Last Name
                <input name="lastName" value={form.lastName} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1" />
              </label>
              <label className="text-sm font-medium">Email
                <input name="email" value={form.email} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1" />
              </label>
              <label className="text-sm font-medium">Phone
                <input name="phone" value={form.phone} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1" />
              </label>
              <label className="text-sm font-medium">Role
                <select name="role" value={form.role} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1">
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </label>
              <label className="text-sm font-medium">Status
                <select name="status" value={form.status} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <label className="text-sm font-medium">Avatar URL
                <input name="avatar" value={form.avatar} onChange={handleChange} className="mt-1 w-full border rounded px-2 py-1" />
              </label>
              <button className="mt-4 bg-green-600 hover:bg-green-700 text-white rounded px-4 py-2 font-semibold" onClick={handleSave} disabled={updating}>
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 